import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { FaGavel, FaSyncAlt, FaExclamationTriangle, FaFileAlt, FaBullhorn, FaCheck, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechTabs, TechButton, TechButtonGhost,
  StatStrip, StatTile, StatLabel, StatValue, StatSub, StatusPill, TechChip, Empty, Loading, ErrorBox,
  borderSoft, mono, accent,
} from '../../styles/techComponents';
import { listProcurement, updateProcurement, makeProcurementContent } from '../../services/procurementService';

const SOSP = { alta: '#ef4444', media: '#f59e0b', baja: '#64748b' };
const money = (n) => (n == null ? null : '$' + Number(n).toLocaleString('es-AR'));

const Card = styled.div` border:1px solid ${borderSoft};border-left:3px solid ${({ $s }) => SOSP[$s] || accent};
  border-radius:8px;padding:0.85rem 1rem;margin-bottom:0.6rem;background:rgba(11,19,32,0.4);
  h5{margin:0 0 0.3rem;font-size:0.95rem} .meta{font-family:${mono};font-size:0.74rem;color:var(--text-secondary);margin:0.25rem 0} `;

export default function Licitaciones() {
  const [data, setData] = useState({ items: [], totals: {} });
  const [tab, setTab] = useState('todas');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [out, setOut] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await listProcurement()); } catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  async function setEstado(it, estado) {
    try { await updateProcurement(it.id, estado); load(); } catch (e) { flash('Error'); }
  }
  async function gen(it, tipo) {
    try { const r = await makeProcurementContent(it.id, tipo); setOut({ it, ...r }); flash('Generado → Bandeja'); load(); }
    catch (e) { flash(e?.response?.data?.error || 'Error'); }
  }

  if (loading) return <TechShell><Loading>Cargando radar…</Loading></TechShell>;
  if (error) return <TechShell><ErrorBox><FaExclamationTriangle /> {error}</ErrorBox></TechShell>;

  const t = data.totals || {};
  const items = (data.items || []).filter((i) => tab === 'todas' ? true : tab === 'altas' ? i.sospecha === 'alta' : i.estado === 'detectado');

  return (
    <TechShell>
      <TechPageHeader
        title="Radar de Licitaciones"
        dossier="CONTROL DEL GASTO"
        subtitle="Vigila la compra pública del municipio y detecta contrataciones sospechosas — material de transparencia y control listo para presentar."
        actions={<TechButtonGhost onClick={load}><FaSyncAlt /> Actualizar</TechButtonGhost>}
      />
      <StatStrip>
        <StatTile><StatLabel>Detectadas</StatLabel><StatValue>{t.total ?? 0}</StatValue><StatSub>en el radar</StatSub></StatTile>
        <StatTile><StatLabel>Sospecha alta</StatLabel><StatValue style={{ color: '#ef4444' }}>{t.altas ?? 0}</StatValue><StatSub>banderas rojas</StatSub></StatTile>
        <StatTile><StatLabel>Sin revisar</StatLabel><StatValue>{t.sin_revisar ?? 0}</StatValue><StatSub>en cola</StatSub></StatTile>
        <StatTile><StatLabel>Monto total</StatLabel><StatValue>{t.monto_total ? money(t.monto_total) : '—'}</StatValue><StatSub>detectado</StatSub></StatTile>
      </StatStrip>
      {msg && <StatusPill style={{ marginBottom: '0.5rem' }}>{msg}</StatusPill>}

      <TechTabs tabs={[{ id: 'todas', label: 'Todas' }, { id: 'altas', label: `Sospecha alta (${t.altas ?? 0})` }, { id: 'cola', label: 'Sin revisar' }]} active={tab} onChange={setTab} />

      <TechSection title="Compras a revisar" icon={<FaGavel />}>
        {items.length === 0 && <Empty>Sin licitaciones detectadas. El radar barre las noticias del municipio a diario.</Empty>}
        {items.map((it) => (
          <Card key={it.id} $s={it.sospecha}>
            <h5>{it.titulo} <TechChip style={{ color: SOSP[it.sospecha], borderColor: SOSP[it.sospecha] }}>{it.sospecha}</TechChip></h5>
            <div className="meta">{[it.tipo?.replace(/_/g, ' '), money(it.monto), it.proveedor, it.area?.replace('_', ' ')].filter(Boolean).join(' · ')}</div>
            {Array.isArray(it.banderas) && it.banderas.length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '0.3rem 0' }}>{it.banderas.map((b) => <TechChip key={b} style={{ color: '#ef4444', borderColor: '#ef4444' }}>🚩 {b.replace(/_/g, ' ')}</TechChip>)}</div>}
            {it.resumen && <p style={{ margin: '0.3rem 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{it.resumen}</p>}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: '0.4rem' }}>
              <TechButton onClick={() => gen(it, 'pedido_informes')}><FaFileAlt /> Pedido de informes</TechButton>
              <TechButtonGhost onClick={() => gen(it, 'gacetilla')}><FaBullhorn /> Gacetilla</TechButtonGhost>
              {it.fuente_url && <TechButtonGhost as="a" href={it.fuente_url} target="_blank" rel="noreferrer"><FaExternalLinkAlt /> fuente</TechButtonGhost>}
              {it.estado === 'detectado' && <TechButtonGhost onClick={() => setEstado(it, 'revisado')}><FaCheck /> revisado</TechButtonGhost>}
              <TechButtonGhost onClick={() => setEstado(it, 'descartado')}><FaTimes /></TechButtonGhost>
            </div>
          </Card>
        ))}
      </TechSection>

      {out && (
        <TechSection title={`Generado: ${out.tipo?.replace(/_/g, ' ')}`} icon={<FaFileAlt />}>
          <div style={{ whiteSpace: 'pre-wrap', fontFamily: mono, fontSize: '0.82rem', border: `1px solid ${borderSoft}`, borderRadius: 8, padding: '1rem', background: 'rgba(11,19,32,0.4)' }}>{out.texto}</div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.4rem' }}>Guardado en la Bandeja de Mensajería (pendiente de aprobación).</p>
        </TechSection>
      )}
    </TechShell>
  );
}
